import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, Subject } from "rxjs";
import { environment } from "../../../environments/environment";
import { AppError } from "../error/error.model";
import { InjectorResolver } from "../injector/injector.service";
import { AppLocalStorage } from "../utils/local-storage.util";
import { Session } from "../session/session.model";
import { TokenService } from "../token/token.service";

// tslint:disable-next-line: no-namespace
export namespace Store {
  export type Path = (() => string) | string;

  export type APIConfig = {
    base?: Path;
    cmsBase?: Path;
    endpoint?: Path;
    defaultBase?: Path;
    base2?: Path;
    base3?: Path;
  };

  export type APIConfigGroup = { [key: string]: Store.APIConfig };

  export interface Config {
    api?: APIConfig;
  }

  export interface State {
    loading: boolean;
    error: AppError;
  }

  export abstract class AbstractState<T> implements State {
    data: null | T;
    loading: boolean;
    error: AppError;
    constructor(data?: T) {
      this.loading = false;
      this.error = null;
      this.data = data || null;
    }
  }

  export abstract class AbstractIdealState<T> extends AbstractState<T> {
    constructor(data?: T) {
      super(data);
    }
  }

  export abstract class AbstractErrorState<T> extends AbstractState<T> {
    constructor(error: AppError, data?: T) {
      super(data);
      this.error = error;
    }
  }
  export abstract class AbstractLoadingState<T> extends AbstractState<T> {
    constructor(data?: T) {
      super(data);
      this.loading = true;
      this.error = null;
    }
  }

  export interface Service {
    config: Config;
  }

  export abstract class AbstractService implements Service {
    config: Store.Config;
    protected controller: Store.Controller;

    constructor() {
      this.controller = new Store.Controller(this);
    }
  }

  export class Controller {
    private http: HttpClient;
    private tokenService: TokenService;

    constructor(protected service: Store.Service) {
      this.http = InjectorResolver.get(HttpClient);
      this.tokenService = InjectorResolver.get(TokenService);
    }

    public get<T>(
      path: Path,
      data?: any,
      base?: Path,
      options?: {},
    ): Observable<T> {
      if (this.http) {
        if (options) {
          options["params"] = data;
          const prepared = this.prepareOptions(options);
          if (prepared instanceof Promise) {
            // Return Observable from Promise
            return new Observable<T>((subscriber) => {
              prepared
                .then((finalOptions) => {
                  this.http
                    .get<T>(this.urlWithBase(path, base), finalOptions)
                    .subscribe({
                      next: (v) => subscriber.next(v as T),
                      error: (e) => subscriber.error(e),
                      complete: () => subscriber.complete(),
                    });
                })
                .catch((e) => subscriber.error(e));
            });
          } else {
            options = prepared;
          }
        }
        return this.http.get<T>(this.urlWithBase(path, base), options);
      }
      return new Subject<T>();
    }

    public put<T>(
      path: Path,
      data: any,
      base?: Path,
      options?: {},
    ): Observable<T> {
      if (this.http) {
        if (options) {
          const prepared = this.prepareOptions(options);
          if (prepared instanceof Promise) {
            return new Observable<T>((subscriber) => {
              prepared
                .then((finalOptions) => {
                  this.http
                    .put<T>(this.urlWithBase(path, base), data, finalOptions)
                    .subscribe({
                      next: (v) => subscriber.next(v as T),
                      error: (e) => subscriber.error(e),
                      complete: () => subscriber.complete(),
                    });
                })
                .catch((e) => subscriber.error(e));
            });
          } else {
            options = prepared;
          }
        }
        return this.http.put<T>(this.urlWithBase(path, base), data, options);
      }
      return new Subject<T>();
    }

    public patch<T>(
      path: Path,
      data: any,
      base?: Path,
      options?: {},
    ): Observable<T> {
      if (this.http) {
        if (options) {
          const prepared = this.prepareOptions(options);

          // Handle asynchronous options preparation
          if (prepared instanceof Promise) {
            return new Observable<T>((subscriber) => {
              prepared
                .then((finalOptions) => {
                  this.http
                    .patch<T>(this.urlWithBase(path, base), data, finalOptions)
                    .subscribe({
                      next: (v) => subscriber.next(v as T),
                      error: (e) => subscriber.error(e),
                      complete: () => subscriber.complete(),
                    });
                })
                .catch((e) => subscriber.error(e));
            });
          } else {
            options = prepared;
          }
        }

        // Standard synchronous execution
        return this.http.patch<T>(this.urlWithBase(path, base), data, options);
      }

      // Fallback if http is not initialized
      return new Subject<T>();
    }

    public post<T>(
      path: Path,
      data?: any,
      base?: Path,
      options?: {},
    ): Observable<T> {
      if (this.http) {
        if (options) {
          const prepared = this.prepareOptions(options);
          if (prepared instanceof Promise) {
            return new Observable<T>((subscriber) => {
              prepared
                .then((finalOptions) => {
                  this.http
                    .post<T>(this.urlWithBase(path, base), data, finalOptions)
                    .subscribe({
                      next: (v) => subscriber.next(v as T),
                      error: (e) => subscriber.error(e),
                      complete: () => subscriber.complete(),
                    });
                })
                .catch((e) => subscriber.error(e));
            });
          } else {
            options = prepared;
          }
        }
        return this.http.post<T>(this.urlWithBase(path, base), data, options);
      }
      return new Subject<T>();
    }

    public delete<T>(path: Path, base?: Path, options?: {}): Observable<T> {
      if (this.http) {
        if (options) {
          const prepared = this.prepareOptions(options);
          if (prepared instanceof Promise) {
            return new Observable<T>((subscriber) => {
              prepared
                .then((finalOptions) => {
                  this.http
                    .delete<T>(this.urlWithBase(path, base), finalOptions)
                    .subscribe({
                      next: (v) => subscriber.next(v as T),
                      error: (e) => subscriber.error(e),
                      complete: () => subscriber.complete(),
                    });
                })
                .catch((e) => subscriber.error(e));
            });
          } else {
            options = prepared;
          }
        }
        return this.http.delete<T>(this.urlWithBase(path, base), options);
      }
      return new Subject<T>();
    }

    public urlWithBase(path: Path, base?: Path): string {
      return this.url(path, base);
    }

    public url(path: Path, base?: Path): string {
      const me = this;

      if (base) {
        base = me.resolvePath(base);
      } else if (
        me.service.config &&
        me.service.config.api &&
        me.service.config.api.base
      ) {
        base = me.resolvePath(me.service.config.api.base);
      } else if (environment.api.core.base) {
        base = me.resolvePath(environment.api.core.base);
      }

      return base + me.sanitiesURL(me.resolvePath(path));
    }

    public getDefaultGetRequestHttpOptions() {
      return {};
    }

    public getDefaultPostRequestHttpOptions() {
      return {};
    }

    public replaceVariables(
      path: Path,
      variables: { [key: string]: string },
    ): string {
      const me = this;
      let newPath: string;

      newPath = me.resolvePath(path);

      Object.keys(variables).forEach((variable: string) => {
        newPath = newPath.replace("{" + variable + "}", variables[variable]);
      });

      return newPath;
    }

    private resolvePath(path: Path): string {
      const me = this;

      if (!path) {
        return undefined;
      }

      if (typeof path === "function") {
        path = path.call(me);
      }
      return path as string;
    }

    private sanitiesURL(url: string): string {
      return url.replace(/\/+/gi, "/");
    }

    private prepareOptions(options?: {}): Promise<{}> {
      // Always get token from TokenService and set Authorization header
      const setAuthHeader = (token: string | null) => {
        if (token) {
          options["headers"] = options["headers"] || ({} as HttpHeaders);
          options["headers"]["Authorization"] = "Bearer " + token;
        }
      };

      return this.tokenService.getAccessToken().then((token) => {
        setAuthHeader(token);
        // Clean up any legacy keys
        if (options["Authorization"]) {
          delete options["Authorization"];
        }
        if (options["Token"]) {
          delete options["Token"];
        }
        return options;
      });
    }
  }
}
